import PageHero from "@/components/base/PageHero";
import SectionLabel from "@/components/base/SectionLabel";
import ContactForm from "@/pages/kontakt/components/ContactForm";
import { brandAssets, company } from "@/mocks/site";

const mapEmbed =
  "https://www.google.com/maps?q=Geschwand%20111%2C%2091286%20Obertrubach%2C%20Deutschland&output=embed";
const routeLink =
  "https://www.google.com/maps/dir/?api=1&destination=Geschwand%20111%2C%2091286%20Obertrubach";

export default function Kontakt() {
  return (
    <>
      <PageHero
        label="Kontakt"
        title="Sprechen Sie mit uns."
        lead="Anfragen landen direkt bei den Menschen, die konstruieren und fertigen. Rufen Sie an oder schreiben Sie uns — wir melden uns kurzfristig zurück."
      />

      <section className="bg-background-50 py-14 md:py-20">
        <div className="mx-auto w-full max-w-shell px-4 md:px-6">
          <div className="grid grid-cols-1 gap-9 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-5">
              <SectionLabel className="mb-5">Direkter Kontakt</SectionLabel>
              <h2 className="font-heading text-[26px] font-extrabold leading-[1.1] tracking-tightest text-foreground-950 sm:text-[30px]">
                {company.shortName} Präzisions-Mechanik
              </h2>

              <div className="relative mt-6 overflow-hidden border border-foreground-800 bg-foreground-950 p-6 text-background-50">
                <div className="blueprint absolute inset-0 opacity-25" />
                <div className="relative">
                  <div className="flex items-center gap-3">
                    <img
                      src={brandAssets.mark}
                      alt={company.name}
                      width={44}
                      height={44}
                      className="h-11 w-11 object-contain"
                    />
                    <p className="font-label text-[10px] uppercase tracking-[0.2em] text-background-400">
                      Firmenanschrift
                    </p>
                  </div>
                  <address className="mt-5 not-italic text-[15px] leading-relaxed text-background-100">
                    <strong className="font-heading font-bold text-background-50">{company.name}</strong>
                    <br />
                    {company.street}
                    <br />
                    D-{company.zip} {company.city}
                  </address>

                  <dl className="mt-6 flex flex-col gap-3 border-t border-foreground-800 pt-5 font-label text-[12.5px]">
                    <div className="flex items-center gap-3">
                      <i className="ri-phone-line text-[14px] text-primary-400" />
                      <dt className="sr-only">Telefon</dt>
                      <dd>
                        <a href={`tel:${company.phoneHref}`} className="hover:text-primary-300">
                          {company.phone}
                        </a>
                      </dd>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-printer-line text-[14px] text-primary-400" />
                      <dt className="sr-only">Telefax</dt>
                      <dd className="text-background-300">{company.fax}</dd>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-mail-line text-[14px] text-primary-400" />
                      <dt className="sr-only">E-Mail</dt>
                      <dd>
                        <a href={`mailto:${company.email}`} className="hover:text-primary-300">
                          {company.email}
                        </a>
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>

              <div className="mt-5 border border-foreground-200 bg-background-100 p-6">
                <p className="font-label text-[10px] uppercase tracking-[0.2em] text-foreground-500">
                  Geschäftszeiten
                </p>
                <dl className="mt-4 flex flex-col divide-y divide-foreground-200">
                  {company.hours.map((entry) => (
                    <div key={entry.days} className="flex items-baseline justify-between gap-4 py-2.5">
                      <dt className="text-[13px] font-medium text-foreground-800">{entry.days}</dt>
                      <dd className="text-right font-label text-[11.5px] text-foreground-600">
                        {entry.time}
                      </dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-4 flex items-start gap-2 text-[12px] leading-relaxed text-foreground-500">
                  <i className="ri-information-line mt-0.5 text-[14px] text-primary-600" />
                  Für die Abholung von Ersatzteilen bitten wir um kurze telefonische Absprache.
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-foreground-200 bg-background-100">
        <div className="mx-auto w-full max-w-shell px-4 py-12 md:px-6 md:py-16">
          <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <SectionLabel className="mb-4">Anfahrt</SectionLabel>
              <h2 className="font-heading text-[22px] font-extrabold tracking-tightest text-foreground-950 md:text-[28px]">
                Geschwand 111, 91286 Obertrubach
              </h2>
            </div>
            <a
              href={routeLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 whitespace-nowrap border border-foreground-300 bg-background-50 px-4 py-2.5 font-heading text-[11px] font-bold uppercase tracking-[0.13em] text-foreground-900 transition-colors hover:border-foreground-950"
            >
              <i className="ri-route-line text-[14px]" />
              Route planen
            </a>
          </div>
          <div className="h-[320px] w-full overflow-hidden border border-foreground-200 bg-background-200 md:h-[420px]">
            <iframe
              title="Standort Ulrich Arzt Präzisions-Mechanik in Obertrubach-Geschwand"
              src={mapEmbed}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              aria-label="Karte des Firmenstandorts Geschwand 111, 91286 Obertrubach"
            />
          </div>
        </div>
      </section>
    </>
  );
}